import React from "react";
import { CloudUpload, Upload } from "lucide-react";
import { cn } from "../../lib/utils.js";
import { shapeClass } from "../../lib/shape.js";
import { FRAME_FOCUS_RING } from "../../lib/field-frame.js";
import { buttonVariants } from "../../atoms/Button/button-variants.js";
import type { FileUploadVariant } from "./FileUpload.js";

/* FileUpload's <label> (internal, not in the barrel). In every variant the
   label wraps the native <input type=file> passed as children, so click, the
   native dialog, keyboard focus and the accessible name all come from the
   input. `dropzone` and `strip` are drop targets and share today's state
   paint; `button` is only a label in the Button look and never takes a drop.
   states-check-ignore: the input is sr-only, so the label draws the ring while
   it is focused: today's focus-within ring on the dropzone, the field-frame
   recipe's FRAME_FOCUS_RING on strip and button. */

type DropVariant = Exclude<FileUploadVariant, "button">;

/* Each drop layout: its box and focus ring, and what sits after the input.
   The dropzone strings are today's classes verbatim and in today's order. */
const DROP_VARIANTS: Record<
  DropVariant,
  {
    box: string;
    ring: string;
    content: (hint: React.ReactNode) => React.ReactNode;
  }
> = {
  dropzone: {
    box: "flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed px-4 py-6 text-center text-sm transition-colors",
    ring: "focus-within:outline-none focus-within:ring-2 focus-within:ring-pb-teal/40",
    content: (hint) => (
      <>
        <CloudUpload
          className="w-6 h-6 text-pb-text-muted"
          aria-hidden="true"
        />
        <span className="text-pb-text-secondary">
          Drag &amp; drop, or{" "}
          <span className="text-pb-teal-text font-medium">browse</span>
        </span>
        {hint != null && (
          <span className="text-dense-1 text-pb-text-muted">{hint}</span>
        )}
      </>
    ),
  },
  /* One line: icon, the hint (or a default instruction), and Browse in the
     xs Button look. The label is the control, so Browse is a span. */
  strip: {
    box: `flex items-center gap-2 ${shapeClass("rounded")} border border-dashed px-3 py-2 text-sm transition-colors`,
    ring: FRAME_FOCUS_RING,
    content: (hint) => (
      <>
        <CloudUpload
          className="w-4 h-4 shrink-0 text-pb-text-muted"
          aria-hidden="true"
        />
        <span className="min-w-0 flex-1 truncate text-pb-text-secondary">
          {hint ?? "Drag & drop to upload"}
        </span>
        <span className={cn(buttonVariants({ size: "xs" }), "shrink-0")}>
          Browse
        </span>
      </>
    ),
  },
};

interface FileUploadSurfaceProps {
  variant: FileUploadVariant;
  disabled?: boolean | undefined;
  invalid?: boolean | undefined;
  hint?: React.ReactNode;
  triggerLabel: string;
  onDropFiles: (files: FileList) => void;
  /** The native <input type=file>. */
  children: React.ReactNode;
}

export function FileUploadSurface({
  variant,
  disabled,
  invalid,
  hint,
  triggerLabel,
  onDropFiles,
  children,
}: FileUploadSurfaceProps): React.ReactElement {
  const [dragging, setDragging] = React.useState(false);

  if (variant === "button") {
    /* A label never matches :disabled, so the disabled dim comes from
       buttonVariants' focusableDisabled plain classes. */
    return (
      <label
        className={cn(
          buttonVariants({ size: "sm", focusableDisabled: disabled }),
          FRAME_FOCUS_RING,
          "cursor-pointer",
        )}
      >
        {children}
        <Upload className="h-3.5 w-3.5" aria-hidden="true" />
        {triggerLabel}
      </label>
    );
  }

  const layout = DROP_VARIANTS[variant];
  return (
    /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions --
       the drag handlers augment the real <input type=file> wrapped inside
       (fully keyboard + click operable); they are not the sole interaction,
       and a label is not a control. */
    <label
      onDragOver={(e) => {
        if (disabled) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        onDropFiles(e.dataTransfer.files);
      }}
      className={cn(
        layout.box,
        `${layout.ring} has-[:disabled]:hover:bg-none`,
        disabled
          ? "opacity-60 cursor-not-allowed border-pb-control-border"
          : "cursor-pointer",
        dragging
          ? "border-pb-teal bg-pb-teal-soft"
          : invalid
            ? "border-pb-red bg-pb-card"
            : "border-pb-control-border bg-pb-card hover:pb-overlay-paint",
      )}
    >
      {children}
      {layout.content(hint)}
    </label>
  );
}
